import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import sessionStorage from "redux-persist/lib/storage/session";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { createFilter } from "redux-persist-transform-filter";
import { combineReducers } from "redux";
import { reducersMap } from "app/reducers";

export const STORAGE_KEY_PREFIX = "pfs";

// Legacy redux-persist v4 prefix. v4 data is migrated into the v6 layout on first client
// load (see migrateLegacyPersistedState) and the old keys are then removed.
const LEGACY_STORAGE_KEY_PREFIX = "reduxPersist";

// `currency` is a primitive-valued slice (e.g. "EUR"), so it cannot be wrapped in
// persistReducer (redux-persist attaches a `_persist` key, which only works on objects).
// It is persisted manually below: sessionStorage, with a global (not per-shop) prefix.
const CURRENCY_STORAGE_KEY = `${STORAGE_KEY_PREFIX}:currency`;
const LEGACY_CURRENCY_STORAGE_KEY = `PFS:${LEGACY_STORAGE_KEY_PREFIX}:currency`;

export const loadPersistedCurrency = () => {
	try {
		const raw = window.sessionStorage.getItem(CURRENCY_STORAGE_KEY);
		return raw === null ? undefined : JSON.parse(raw);
	} catch (error) {
		return undefined;
	}
};

export const persistCurrencyOnChange = store => {
	let previousCurrency = store.getState().currency;
	return store.subscribe(() => {
		const currency = store.getState().currency;
		if (currency !== previousCurrency) {
			previousCurrency = currency;
			try {
				window.sessionStorage.setItem(CURRENCY_STORAGE_KEY, JSON.stringify(currency));
			} catch (error) {
				// ignore write errors (private mode / quota exceeded)
			}
		}
	});
};

// Slices persisted to localStorage via the root (whole-state) persist.
// The transforms below are key-scoped (createFilter only runs for its named slice),
// so they apply correctly during the per-slice serialization of the root persist.
const LOCAL_STORAGE_WHITELIST = [
	"auth",
	"email",
	"isMapPolicyAccepted",
	"profile",
	"coupons",
	"booking",
	"sponsorship",
	"exchangeRate",
];

// id, uri pour les info produit dans le tunnel + analytics lors d'un reload
const profileFilter = createFilter("profile", ["newsletterPreference.shouldDisplayFrequencyModal"]);
const couponsFilter = createFilter("coupons", [
	"showCreditsNotification",
	"showCreditNotesNotification",
]);
// on ne persiste en localStorage que le nombre de voyageurs (le reste va en sessionStorage)
const bookingWhitelistFilter = createFilter("booking", [
	"passengers",
	"adults",
	"children",
	"infants",
]);
const sponsorshipWhitelistFilter = createFilter("sponsorship", ["canInviteByEmailAt"]);

const LOCAL_STORAGE_TRANSFORMS = [
	profileFilter,
	couponsFilter,
	bookingWhitelistFilter,
	sponsorshipWhitelistFilter,
];

// Slices persisted individually to sessionStorage.
// `whitelist`/`blacklist` here filter the top-level keys of each slice (redux-persist v6
// applies them per slice), replacing the v4 createFilter transforms which targeted slice keys.
const SESSION_SLICE_CONFIG = {
	ficheProduit: { whitelist: ["id", "uri"] },
	quotation: {},
	hotelOnly: {},
	navigation: { whitelist: ["isInApp"] },
	payment: {
		whitelist: [
			"insurances",
			"timestamp",
			"paymentTypes",
			"paymentModes",
			"dateOfBirthRequired",
			"showFrequentTravellerCardInput",
			"paymentSwissbilling",
			"priceChange",
			"deltaBasePrice",
		],
	},
	partnerData: {},
	merchandising: { whitelist: ["current"] },
	partner: { whitelist: ["strategy"] },
	smartDP: {
		whitelist: [
			"accommodation",
			"timestamp",
			"code",
			"status",
			"specialOffer",
			"specialOfferInfos",
		],
	},
	marketing: {
		whitelist: ["source", "utmMedium", "utmSource", "utmCampaign", "utmContent", "utmTerm"],
	},
	searchMyBooking: {},
	// on persiste le reste de booking (hors nombre de voyageurs) en sessionStorage
	booking: { blacklist: ["passengers", "adults", "children", "infants"] },
	// on persiste les infos code promo pour qu'apres un 3DS de retour vers la page de paiement
	// on recupere les infos de code promo pour l'event payment_confirmation
	promotion: {},
};

/**
 * Builds the persisted root reducer (client-side only).
 *
 * Three storage targets, mirroring the legacy redux-persist v4 setup:
 *  - localStorage  : whole-state persist (root) for {@link LOCAL_STORAGE_WHITELIST}
 *  - sessionStorage: each slice in {@link SESSION_SLICE_CONFIG} wrapped individually
 *  - sessionStorage: `currency`, stored globally (not per shop)
 *
 * Nesting happens at different tree levels (root + individual slices), so the reserved
 * `_persist` key never collides.
 */
export const buildPersistedRootReducer = shop => {
	const keyPrefix = `${STORAGE_KEY_PREFIX}:${shop}:`;
	const wrappedReducers = { ...reducersMap };

	Object.entries(SESSION_SLICE_CONFIG).forEach(([sliceKey, { whitelist, blacklist }]) => {
		wrappedReducers[sliceKey] = persistReducer(
			{ key: sliceKey, keyPrefix, storage: sessionStorage, whitelist, blacklist },
			reducersMap[sliceKey]
		);
	});

	// currency is persisted manually (see persistCurrencyOnChange) because it is a primitive slice.
	const rootReducer = combineReducers(wrappedReducers);

	return persistReducer(
		{
			key: "root",
			keyPrefix,
			storage,
			whitelist: LOCAL_STORAGE_WHITELIST,
			transforms: LOCAL_STORAGE_TRANSFORMS,
			// The createFilter transforms persist only sub-parts of each whitelisted slice
			// (e.g. profile -> just newsletterPreference.shouldDisplayFrequencyModal). The default
			// autoMergeLevel1 would hard-replace the whole slice on rehydrate, wiping the rest
			// (e.g. profile.name). autoMergeLevel2 merges one level into each slice, preserving the
			// reducer's other keys — matching the legacy v4 autoRehydrate behavior. This also keeps
			// the session-persisted booking fields intact when the localStorage booking filter
			// (passengers/adults/children/infants) rehydrates over them.
			stateReconciler: autoMergeLevel2,
		},
		rootReducer
	);
};

// redux-persist v6 stores each persisted value as an independently JSON-stringified entry,
// next to a `_persist` metadata key. version -1 matches the (unset) persistConfig version.
const buildPersistMeta = () => JSON.stringify({ version: -1, rehydrated: true });

// localStorage (v4): each whitelisted slice under its own `…:{slice}` key, value = the whole
// slice as one JSON string. localStorage (v6): a single `…:root` object, each slice value kept
// as a JSON string — i.e. the v4 per-slice value is already in v6's shape, copied verbatim.
const migrateLegacyLocalStorage = (legacyPrefix, shop) => {
	try {
		const newRootKey = `${STORAGE_KEY_PREFIX}:${shop}:root`;
		const root = JSON.parse(window.localStorage.getItem(newRootKey)) || {};
		let migratedAny = false;
		LOCAL_STORAGE_WHITELIST.forEach(slice => {
			const legacyValue = window.localStorage.getItem(`${legacyPrefix}${slice}`);
			// Don't clobber a value the v6 store may already have written for this slice.
			if (legacyValue !== null && root[slice] === undefined) {
				root[slice] = legacyValue;
				migratedAny = true;
			}
		});
		if (!migratedAny) {
			return;
		}
		if (root._persist === undefined) {
			root._persist = buildPersistMeta();
		}
		window.localStorage.setItem(newRootKey, JSON.stringify(root));
		LOCAL_STORAGE_WHITELIST.forEach(slice => {
			window.localStorage.removeItem(`${legacyPrefix}${slice}`);
		});
	} catch (error) {
		// leave legacy keys in place on failure; nothing is lost, migration just no-ops
	}
};

// sessionStorage (v4): each slice under its own `…:{slice}` key, value = the whole slice as one
// JSON string. sessionStorage (v6): same per-slice key, but the value is an object where each
// slice KEY is independently JSON-stringified, plus `_persist`.
const migrateLegacySessionStorage = (legacyPrefix, shop) => {
	Object.keys(SESSION_SLICE_CONFIG).forEach(slice => {
		try {
			const legacyKey = `${legacyPrefix}${slice}`;
			const raw = window.sessionStorage.getItem(legacyKey);
			if (raw === null) {
				return;
			}
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== "object") {
				window.sessionStorage.removeItem(legacyKey);
				return;
			}
			const migrated = {};
			Object.keys(parsed).forEach(key => {
				migrated[key] = JSON.stringify(parsed[key]);
			});
			migrated._persist = buildPersistMeta();
			window.sessionStorage.setItem(
				`${STORAGE_KEY_PREFIX}:${shop}:${slice}`,
				JSON.stringify(migrated)
			);
			window.sessionStorage.removeItem(legacyKey);
		} catch (error) {
			// skip this slice; a corrupt/oversized entry shouldn't block the rest
		}
	});
};

// currency had no transform in v4, so the stored value is already `JSON.stringify(currency)` —
// exactly what loadPersistedCurrency expects. Only the key (prefix) changes.
const migrateLegacyCurrency = () => {
	try {
		const raw = window.sessionStorage.getItem(LEGACY_CURRENCY_STORAGE_KEY);
		if (raw === null) {
			return;
		}
		if (window.sessionStorage.getItem(CURRENCY_STORAGE_KEY) === null) {
			window.sessionStorage.setItem(CURRENCY_STORAGE_KEY, raw);
		}
		window.sessionStorage.removeItem(LEGACY_CURRENCY_STORAGE_KEY);
	} catch (error) {
		// ignore
	}
};

/**
 * One-time migration of legacy redux-persist v4 storage to the v6 layout, per shop.
 *
 * Reads the old `reduxPersist:{shop}:*` (and `PFS:reduxPersist:currency`) entries, rewrites them
 * into the v6 `pfs:` keys/shape, then deletes the originals so the migration never runs twice and
 * the old footprint is reclaimed. Guarded by a per-shop flag; every storage access is wrapped in
 * try/catch so private mode / disabled storage simply falls back to a clean v6 start.
 *
 * Must run before persistStore rehydrates (called from configureStore on the client).
 */
export const migrateLegacyPersistedState = shop => {
	if (typeof window === "undefined" || !shop) {
		return;
	}
	const flagKey = `${STORAGE_KEY_PREFIX}:migrated:${shop}`;
	try {
		if (window.localStorage.getItem(flagKey) !== null) {
			return;
		}
	} catch (error) {
		return;
	}

	const legacyPrefix = `${LEGACY_STORAGE_KEY_PREFIX}:${shop}:`;
	migrateLegacyLocalStorage(legacyPrefix, shop);
	migrateLegacySessionStorage(legacyPrefix, shop);
	migrateLegacyCurrency();

	try {
		window.localStorage.setItem(flagKey, "1");
	} catch (error) {
		// flag write failed (quota / private mode); the deletions above already prevent a second
		// run from finding anything to copy, so this is non-fatal.
	}
};
